// Group-picker helpers shared by SingleForm, BatchForm, and AIForm.
//
// The composer's "primary group" / "default group" pickers all default
// to the **private** state (no group attached) — same posture across all
// three modes so a user paste-batch-creating events doesn't get them
// silently bound to whichever group happened to be alphabetically first.
//
// We bake the "Private (just me)" affordance into the option list as a
// sentinel value; doc-ui's `Select` doesn't surface a separate
// no-selection slot and a missing trigger label is unfriendly. The
// sentinel maps back to a literal `null` groupId on submit.

import type { SelectOption } from '@viquard/doc-ui/components';
import type { CalendarGroup } from '../../types.js';

/** Sentinel value for the "Private (just me)" row in primary/default-
 *  group Selects. Picked to be unguessable so it can't collide with a
 *  group UUID. */
export const PRIVATE_GROUP_SENTINEL = '__private__';

/** Build the option list for a primary/default group Select, prepending
 *  the "Private" sentinel row. Used by both SingleForm (per-event
 *  primary-group picker) and BatchForm/AIForm (batch-level default
 *  group fallback). */
export function buildDefaultGroupOptions(
  groups: readonly CalendarGroup[] | undefined,
  privateLabel: string,
): readonly SelectOption[] {
  return [
    {
      value: PRIVATE_GROUP_SENTINEL,
      label: privateLabel,
      searchText: privateLabel,
    },
    ...(groups ?? []).map((g) => ({
      value: g.id,
      label: g.name,
      searchText: g.name,
    })),
  ];
}
